import React, { useEffect, useState } from "react";
import resolveImageUrl from "@/lib/imageUrl";
import ImagePicker from "./ImagePicker";

// Editorial CMS for /events. Same UX contract as MembershipAdmin: global
// state (draft | live), bulk publish w/ pre-flight analysis, per-section
// drawer editor with EN/DA/ES tabs and reused Media Library.

const META = {
  hero:                "01 · Hero",
  next_event:          "02 · Next event",
  experience_pillars:  "03 · What our events are about",
  past_events:         "04 · Past events",
  social_run_feature:  "05 · Social Run feature",
  origin:              "06 · Where it started · DANA 2024",
  whats_next:          "07 · What comes next",
  join:                "08 · Join",
  final_cta:           "09 · Final CTA",
};

const LANGS = [
  { code: "en", label: "English" },
  { code: "da", label: "Dansk" },
  { code: "es", label: "Español" },
];

const IMAGE_REQUIRED = { hero: 1, social_run_feature: 4, origin: 1, final_cta: 1 };

export default function EventsPageAdmin({ authed }) {
  const [sections, setSections] = useState(null);
  const [settings, setSettings] = useState(null);
  const [events, setEvents] = useState([]);
  const [analysis, setAnalysis] = useState({});
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [publishModal, setPublishModal] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const [rSec, rSet, rAna, rPrev] = await Promise.all([
      authed.get("/content?kind=events_page_section&status="),
      authed.get("/admin/events-page/settings"),
      authed.get("/admin/events-page/analysis"),
      authed.get("/admin/events-page/preview"),
    ]);
    const rows = (rSec.data || []).slice().sort((a, b) => ((a.data?.order || 0) - (b.data?.order || 0)));
    setSections(rows);
    setSettings(rSet.data || {});
    setEvents(rPrev.data.events || []);
    const byId = {};
    (rAna.data.sections || []).forEach(s => { byId[s.id] = s; });
    setAnalysis(byId);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const setState = async (nextState) => {
    const cur = settings?.state || "draft";
    if (nextState === cur) return;
    const prompts = {
      draft: "Take /events OFFLINE (back to the legacy list)? No content is deleted.",
      live:  "Publish the new /events editorial hub? Anyone visiting /events will see it. The legacy list is only used as a fallback if this is set to DRAFT later.",
    };
    if (!window.confirm(prompts[nextState])) return;
    const r = await authed.put("/admin/events-page/settings", { state: nextState });
    setSettings(r.data);
    setMsg(nextState === "live" ? "/events hub is now LIVE." : "/events reverted to the legacy list.");
    setTimeout(() => setMsg(""), 3500);
  };

  const setFeatured = async (slug) => {
    const r = await authed.put("/admin/events-page/settings", { featured_next_event_slug: slug || "" });
    setSettings(r.data);
    setMsg(slug ? `Next event pinned to ${slug}.` : "Next event switched to auto-pick.");
    setTimeout(() => setMsg(""), 3500);
  };

  const toggleSelected = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openPublishModal = async (scope) => {
    const ids = scope === "selected" ? Array.from(selected) : null;
    const r = await authed.post("/admin/events-page/publish-batch", { ids, dry_run: true });
    setPublishModal({ scope, ids, dry: r.data });
  };
  const confirmPublish = async () => {
    const { scope, ids } = publishModal;
    setPublishModal({ ...publishModal, busy: true });
    const r = await authed.post("/admin/events-page/publish-batch", { ids: scope === "selected" ? ids : null });
    setPublishModal({ ...publishModal, result: r.data });
    setSelected(new Set());
    await load();
  };

  if (sections === null || !settings) return <div className="text-ink/50 text-sm">Loading…</div>;

  const state = settings.state || "draft";
  const stateColor = state === "live" ? "bg-emerald-100 text-emerald-800" : "bg-line/30 text-ink/70";
  const stateCopy = state === "live"
    ? "/events shows the NEW editorial hub. The legacy list is only used if state is set back to DRAFT."
    : "/events currently shows the LEGACY list. Only admins with ?preview=1 see the new editorial hub.";

  const publishedCount = sections.filter(s => s.status === "published" && s.data?.visible !== false).length;
  const draftPendingCount = sections.filter(s => !!s.data_draft).length;

  return (
    <div className="space-y-6" data-testid="events-page-admin">
      {/* ── Global state card ── */}
      <section className="border border-line rounded-xl p-6 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-[280px]">
            <div className="label-mono text-terracotta text-[10px]">EVENTS HUB · /events</div>
            <h2 className="font-editorial text-2xl md:text-3xl tracking-tighter mt-1">Events page</h2>
            <div className="mt-3 flex items-center gap-3">
              <span data-testid="events-page-state-badge"
                    className={`text-[10px] label-mono tracking-widest px-2 py-1 rounded ${stateColor}`}>
                {state.toUpperCase()}
              </span>
              <a href="/events?preview=1" target="_blank" rel="noreferrer"
                 data-testid="events-page-preview-link"
                 className="text-[11px] underline text-ink/70 hover:text-ink">Open admin preview ↗</a>
            </div>
            <p className="mt-3 text-xs text-ink/60 max-w-xl">{stateCopy}</p>
          </div>
          <div className="flex flex-col items-end gap-2 min-w-[220px]">
            <div className="text-[11px] text-ink/60">
              <span className="text-emerald-700 font-medium">{publishedCount}</span> published
              {" · "}
              <span className="text-amber-700 font-medium">{draftPendingCount}</span> with pending drafts
            </div>
            <div className="flex gap-2">
              <button onClick={() => setState("draft")} disabled={state === "draft"}
                      data-testid="events-page-state-draft"
                      className={`px-3 py-2 text-[11px] uppercase tracking-widest border ${state === "draft" ? "border-line text-ink/40 bg-line/10 cursor-default" : "border-ink text-ink hover:bg-ink hover:text-white"}`}>
                {state === "live" ? "Revert to legacy list" : "Draft"}
              </button>
              <button onClick={() => setState("live")} disabled={state === "live"}
                      data-testid="events-page-state-live"
                      className={`px-3 py-2 text-[11px] uppercase tracking-widest ${state === "live" ? "bg-emerald-600 text-white cursor-default" : "bg-terracotta text-white hover:bg-terracotta-dark"}`}>
                Publish new /events
              </button>
            </div>
            {msg && <div className="text-[11px] text-ink/60">{msg}</div>}
          </div>
        </div>
      </section>

      {/* ── Featured Next Event picker ── */}
      <section className="border border-line rounded-xl p-6 bg-white" data-testid="events-page-featured">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="label-mono text-terracotta text-[10px]">SINGLE SOURCE OF TRUTH</div>
            <h3 className="font-editorial text-xl tracking-tight mt-1">Featured Next Event</h3>
            <p className="text-xs text-ink/60 mt-1 max-w-xl">
              Pin one existing event or leave blank so the hub auto-picks the nearest upcoming event by date. Events are managed under their own Admin tabs (KB Hallen, Social Run). Nothing is duplicated here.
            </p>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <select value={settings.featured_next_event_slug || ""}
                  onChange={e => setFeatured(e.target.value)}
                  data-testid="events-page-featured-select"
                  className="border border-line px-3 py-2 bg-white text-sm min-w-[280px]">
            <option value="">— Auto-pick nearest upcoming —</option>
            {events.map(e => (
              <option key={e.slug} value={e.slug}>
                {e.slug} · {e.date_iso} · {e.page_state || "n/a"}
              </option>
            ))}
          </select>
          {settings.featured_next_event_slug && (
            <button onClick={() => setFeatured("")}
                    data-testid="events-page-featured-clear"
                    className="text-[11px] uppercase tracking-widest text-ink/60 hover:text-ink">Clear pin</button>
          )}
        </div>
      </section>

      {/* ── Bulk publish ── */}
      <section className="border border-line rounded-xl p-4 bg-white flex flex-wrap items-center gap-3">
        <div className="text-[11px] text-ink/60 flex-1 min-w-[220px]">
          <span className="font-medium text-ink">Bulk publish</span> · publishes section content only — does not change page state.
          {selected.size > 0 && <span className="ml-2 text-terracotta">{selected.size} selected</span>}
        </div>
        <button onClick={() => openPublishModal("selected")} disabled={selected.size === 0}
                data-testid="events-page-publish-selected"
                className={`px-3 py-2 text-[11px] uppercase tracking-widest border ${selected.size === 0 ? "border-line text-ink/30 cursor-default" : "border-ink text-ink hover:bg-ink hover:text-white"}`}>
          Publish selected
        </button>
        <button onClick={() => openPublishModal("all")}
                data-testid="events-page-publish-all"
                className="px-3 py-2 text-[11px] uppercase tracking-widest bg-ink text-white hover:bg-black">
          Publish all sections
        </button>
      </section>

      {/* ── Sections ── */}
      <div className="space-y-2" data-testid="events-page-sections-list">
        {sections.map((s, idx) => (
          <SectionRow key={s.id} s={s} idx={idx}
                      onEdit={() => setEditing(s)}
                      analysis={analysis[s.id]}
                      checked={selected.has(s.id)}
                      onToggleCheck={() => toggleSelected(s.id)} />
        ))}
      </div>

      {editing && (
        <SectionEditor s={editing} authed={authed}
                       onClose={() => { setEditing(null); load(); }} />
      )}

      {publishModal && (
        <PublishBatchModal modal={publishModal} onConfirm={confirmPublish} onClose={() => setPublishModal(null)} />
      )}
    </div>
  );
}

function SectionRow({ s, idx, onEdit, analysis, checked, onToggleCheck }) {
  const d = s.data || {};
  const thumb = d.images?.[0]?.url;
  const hidden = s.status !== "published" || d.visible === false;
  const hasDraft = !!s.data_draft;
  const name = META[d.slug] || d.slug;
  const a = analysis || {};
  const missingLangs = a.missing_translation || [];
  return (
    <div data-testid={`events-page-row-${d.slug}`}
         className={`bg-white border ${hidden ? "border-dashed border-line/60" : "border-line"} rounded-lg flex items-center gap-3 p-3`}>
      <input type="checkbox" checked={!!checked} onChange={onToggleCheck}
             data-testid={`events-page-check-${d.slug}`}
             className="w-4 h-4 cursor-pointer accent-terracotta" onClick={(e) => e.stopPropagation()} />
      <div className="font-editorial text-lg tabular-nums w-8 text-ink/40">{idx + 1}</div>
      <div className="w-14 h-14 shrink-0 border border-line/50 bg-line/10 overflow-hidden">
        {thumb ? <img src={resolveImageUrl(thumb)} alt="" className="w-full h-full object-cover" />
               : <div className="w-full h-full flex items-center justify-center text-ink/25 text-[10px]">—</div>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-editorial text-base truncate">{name}</div>
        <div className="text-[11px] text-ink/50 truncate">{d.template} · {(d.headline?.en || "").slice(0, 70)}</div>
        {(a.missing_image || a.missing_headline || missingLangs.length > 0) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {a.missing_headline && <span className="text-[9px] label-mono tracking-widest px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">NO HEADLINE</span>}
            {a.missing_image && <span className="text-[9px] label-mono tracking-widest px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">MISSING IMAGE</span>}
            {missingLangs.map(l => (
              <span key={l} className="text-[9px] label-mono tracking-widest px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">MISSING {l.toUpperCase()}</span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {hasDraft && <span className="text-[9px] label-mono tracking-widest px-2 py-1 rounded bg-amber-100 text-amber-800">DRAFT</span>}
        <span className={`text-[10px] label-mono tracking-widest px-2 py-1 rounded ${hidden ? "bg-line/30 text-ink/60" : "bg-emerald-100 text-emerald-800"}`}>
          {hidden ? (s.status === "published" ? "HIDDEN" : "DRAFT") : "PUBLISHED"}
        </span>
        <button onClick={onEdit} data-testid={`events-page-edit-${d.slug}`}
                className="border border-ink text-ink px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-ink hover:text-white">Edit</button>
      </div>
    </div>
  );
}

// ─── Section editor drawer ────────────────────────────────────────────
function SectionEditor({ s, authed, onClose }) {
  const [data, setData] = useState(s.data_draft || s.data || {});
  const [lang, setLang] = useState("en");
  const [pickerIdx, setPickerIdx] = useState(null);
  const [pillarPicker, setPillarPicker] = useState(null); // pillar index
  const [busy, setBusy] = useState(false);

  const patch = (k, v) => setData(prev => ({ ...prev, [k]: v }));
  const patchLang = (k, v) => setData(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [lang]: v } }));
  const patchImage = (idx, obj) => {
    const imgs = [...(data.images || [])];
    imgs[idx] = { ...(imgs[idx] || {}), ...obj };
    patch("images", imgs);
  };
  const patchPillar = (idx, obj) => {
    const pillars = [...(data.pillars || [])];
    pillars[idx] = { ...(pillars[idx] || {}), ...obj };
    patch("pillars", pillars);
  };
  const patchPillarLang = (idx, key, v) => {
    const pillars = [...(data.pillars || [])];
    const cur = pillars[idx] || {};
    pillars[idx] = { ...cur, [key]: { ...(cur[key] || {}), [lang]: v } };
    patch("pillars", pillars);
  };
  const patchStat = (i, k, v) => {
    const stats = [...(data.stats || [])];
    stats[i] = { ...(stats[i] || {}), [k]: v };
    patch("stats", stats);
  };
  const patchStatLabel = (i, v) => {
    const stats = [...(data.stats || [])];
    stats[i] = { ...(stats[i] || {}), label: { ...((stats[i] || {}).label || {}), [lang]: v } };
    patch("stats", stats);
  };

  const saveDraft = async () => {
    setBusy(true);
    await authed.post(`/content/${s.id}/draft`, { data });
    setBusy(false);
  };
  const publish = async () => {
    setBusy(true);
    await authed.post(`/content/${s.id}/publish`, { data });
    setBusy(false);
    onClose();
  };
  const discardDraft = async () => {
    if (!window.confirm("Discard unsaved draft? This cannot be undone.")) return;
    setBusy(true);
    await authed.post(`/content/${s.id}/discard-draft`);
    setBusy(false);
    onClose();
  };
  const toggleVisible = () => patch("visible", data.visible === false ? true : false);

  const t = data.template;
  const imgCount = IMAGE_REQUIRED[t] || 0;
  const primaryImage = (data.images || [])[0];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" role="dialog">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" data-testid="events-page-editor">
        <div className="sticky top-0 bg-white border-b border-line px-6 py-4 flex items-center justify-between">
          <div>
            <div className="label-mono text-terracotta text-[10px]">EDIT SECTION</div>
            <div className="font-editorial text-xl">{META[data.slug] || data.slug}</div>
          </div>
          <button onClick={onClose} className="text-2xl text-ink/50 hover:text-ink px-2">×</button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex gap-2">
            {LANGS.map(l => (
              <button key={l.code} onClick={() => setLang(l.code)}
                      data-testid={`events-page-lang-${l.code}`}
                      className={`px-3 py-1 text-[11px] uppercase tracking-widest ${lang === l.code ? "bg-ink text-white" : "border border-line text-ink/60 hover:border-ink hover:text-ink"}`}>
                {l.label}
              </button>
            ))}
          </div>

          <Field label={`Eyebrow (${lang})`}>
            <input value={(data.eyebrow || {})[lang] || ""} onChange={e => patchLang("eyebrow", e.target.value)}
                   className="border border-line px-3 py-2 w-full label-mono tracking-widest text-sm" />
          </Field>
          <Field label={`Headline (${lang})`}>
            <textarea value={(data.headline || {})[lang] || ""} onChange={e => patchLang("headline", e.target.value)}
                      rows={2} className="border border-line px-3 py-2 w-full font-editorial text-2xl leading-tight" />
          </Field>
          <Field label={`Body (${lang})`}>
            <textarea value={(data.body || {})[lang] || ""} onChange={e => patchLang("body", e.target.value)}
                      rows={8} className="border border-line px-3 py-2 w-full text-sm leading-relaxed" />
          </Field>

          {(t === "hero" || t === "next_event" || t === "join" || t === "final_cta" || t === "social_run_feature" || t === "past_events" || t === "origin") && (
            <>
              <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-line/50">
                <div>
                  <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-1">Primary CTA</div>
                  <input value={(data.cta_label || {})[lang] || ""} onChange={e => patchLang("cta_label", e.target.value)}
                         placeholder={`label (${lang})`} className="w-full border border-line px-3 py-2 text-sm" />
                  <input value={data.cta_url || ""} onChange={e => patch("cta_url", e.target.value)}
                         placeholder="URL (e.g. /events/social-run-for-spain or #next-event)"
                         className="mt-2 w-full border border-line px-3 py-2 text-sm font-mono" />
                </div>
                {(t === "hero" || t === "join" || t === "next_event") && (
                  <div>
                    <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-1">Secondary CTA</div>
                    <input value={(data.cta2_label || {})[lang] || ""} onChange={e => patchLang("cta2_label", e.target.value)}
                           placeholder={`label (${lang})`} className="w-full border border-line px-3 py-2 text-sm" />
                    <input value={data.cta2_url || ""} onChange={e => patch("cta2_url", e.target.value)}
                           placeholder="URL"
                           className="mt-2 w-full border border-line px-3 py-2 text-sm font-mono" />
                  </div>
                )}
              </div>
            </>
          )}

          {t === "experience_pillars" && (
            <div className="pt-2 border-t border-line/50 space-y-3">
              <div className="label-mono text-[11px] tracking-widest text-ink/60">Experience categories ({lang})</div>
              {(data.pillars || []).map((p, i) => (
                <div key={p.key || i} className="border border-line rounded p-3 space-y-2">
                  <input value={(p.name || {})[lang] || ""}
                         onChange={e => patchPillarLang(i, "name", e.target.value)}
                         placeholder="Name" className="w-full border border-line px-3 py-2 text-sm font-editorial" />
                  <textarea value={(p.text || {})[lang] || ""}
                            onChange={e => patchPillarLang(i, "text", e.target.value)}
                            rows={2} placeholder="Short description"
                            className="w-full border border-line px-3 py-2 text-sm" />
                  <div className="flex items-center gap-2">
                    <label className="text-[10px] uppercase tracking-widest text-ink/60 flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={p.visible !== false}
                             onChange={e => patchPillar(i, { visible: e.target.checked })} />
                      Visible
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}

          {t === "social_run_feature" && (
            <div className="pt-2 border-t border-line/50 space-y-3">
              <div className="label-mono text-[11px] tracking-widest text-ink/60">Stats ({lang} label)</div>
              {(data.stats || []).map((st, i) => (
                <div key={i} className="flex gap-2">
                  <input value={st.value || ""} onChange={e => patchStat(i, "value", e.target.value)}
                         className="border border-line px-3 py-2 w-40 font-editorial" placeholder="200+" />
                  <input value={(st.label || {})[lang] || ""} onChange={e => patchStatLabel(i, e.target.value)}
                         className="border border-line px-3 py-2 flex-1" placeholder="label" />
                </div>
              ))}
            </div>
          )}

          {imgCount > 0 && (
            <div className="border-t border-line pt-6">
              <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-3">
                {imgCount === 1 ? "IMAGE" : `IMAGES (up to ${imgCount})`}
              </div>
              <div className="space-y-4">
                {Array.from({ length: imgCount }).map((_, idx) => {
                  const im = (data.images || [])[idx] || {};
                  return (
                    <div key={idx} className="space-y-2">
                      {im.url ? (
                        <>
                          <div className="relative aspect-[16/9] bg-line/10 overflow-hidden">
                            <img src={resolveImageUrl(im.url)} alt=""
                                 className="w-full h-full object-cover"
                                 style={{ objectPosition: `${im.focal?.x ?? 50}% ${im.focal?.y ?? 50}%` }} />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <FocalGrid label="Desktop focal" value={im.focal}
                                       onChange={f => patchImage(idx, { focal: f })} />
                            <FocalGrid label="Mobile focal" value={im.focal_mobile}
                                       onChange={f => patchImage(idx, { focal_mobile: f })} />
                          </div>
                          <input value={(im.alt || {})[lang] || ""}
                                 onChange={e => patchImage(idx, { alt: { ...(im.alt || {}), [lang]: e.target.value } })}
                                 placeholder={`Alt text (${lang})`}
                                 className="w-full border border-line px-3 py-2 text-sm" />
                          <div className="flex gap-2">
                            <button onClick={() => setPickerIdx(idx)}
                                    className="border border-ink text-ink px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-ink hover:text-white">Replace</button>
                            <button onClick={() => patchImage(idx, { url: "" })}
                                    className="border border-line text-ink/60 px-3 py-1.5 text-[11px] uppercase tracking-widest hover:border-rose-400 hover:text-rose-600">Remove</button>
                          </div>
                        </>
                      ) : (
                        <button onClick={() => setPickerIdx(idx)}
                                data-testid={`events-page-pick-${idx}`}
                                className="border-2 border-dashed border-line px-6 py-8 w-full text-ink/50 hover:border-ink hover:text-ink text-sm">
                          + Photo slot {idx + 1} — select from Media Library
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="border-t border-line pt-6 flex items-center justify-between">
            <div>
              <div className="label-mono text-[11px] tracking-widest text-ink/60">VISIBILITY</div>
              <div className="text-sm mt-1">{data.visible === false ? "Hidden from public /events" : "Visible when page is live"}</div>
            </div>
            <button onClick={toggleVisible}
                    data-testid="events-page-visible-toggle"
                    className={`px-3 py-2 text-[11px] uppercase tracking-widest ${data.visible === false ? "bg-ink text-white" : "border border-ink text-ink hover:bg-ink hover:text-white"}`}>
              {data.visible === false ? "Show" : "Hide"}
            </button>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-line px-6 py-4 flex flex-wrap gap-2 justify-end">
          {s.data_draft && (
            <button onClick={discardDraft} disabled={busy}
                    className="px-4 py-2 text-[11px] uppercase tracking-widest border border-rose-300 text-rose-600 hover:bg-rose-50">
              Discard draft
            </button>
          )}
          <button onClick={saveDraft} disabled={busy}
                  data-testid="events-page-save-draft"
                  className="px-4 py-2 text-[11px] uppercase tracking-widest border border-ink text-ink hover:bg-ink hover:text-white">
            Save draft
          </button>
          <button onClick={publish} disabled={busy}
                  data-testid="events-page-publish"
                  className="px-4 py-2 text-[11px] uppercase tracking-widest bg-terracotta text-white hover:bg-terracotta-dark">
            {busy ? "…" : "Publish"}
          </button>
        </div>
      </div>

      {pickerIdx !== null && (
        <ImagePicker onPick={(asset) => {
          const cur = (data.images || [])[pickerIdx] || {};
          patchImage(pickerIdx, {
            url: asset.url,
            alt: cur.alt || { en: asset.alt_text || "", da: asset.alt_text || "", es: asset.alt_text || "" },
          });
          setPickerIdx(null);
        }} onClose={() => setPickerIdx(null)} authed={authed} />
      )}
    </div>
  );
}

function Field({ label, children, span }) {
  return (
    <label className={`block ${span === 3 ? "md:col-span-3" : ""}`}>
      <div className="label-mono text-[11px] tracking-widest text-ink/60 mb-1">{label}</div>
      {children}
    </label>
  );
}

function FocalGrid({ label, value, onChange }) {
  const points = [
    { x: 0, y: 0 }, { x: 50, y: 0 }, { x: 100, y: 0 },
    { x: 0, y: 50 }, { x: 50, y: 50 }, { x: 100, y: 50 },
    { x: 0, y: 100 }, { x: 50, y: 100 }, { x: 100, y: 100 },
  ];
  const cur = value || { x: 50, y: 50 };
  return (
    <div>
      <div className="label-mono text-[10px] tracking-widest text-ink/60 mb-1">{label}</div>
      <div className="grid grid-cols-3 gap-1 w-fit border border-line p-1">
        {points.map(p => (
          <button key={`${p.x}-${p.y}`} onClick={() => onChange(p)}
                  className={`w-6 h-6 rounded-full ${cur.x === p.x && cur.y === p.y ? "bg-terracotta" : "bg-line/30 hover:bg-ink/40"}`} />
        ))}
      </div>
    </div>
  );
}

function PublishBatchModal({ modal, onConfirm, onClose }) {
  const { dry, result, busy, scope } = modal;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="dialog" data-testid="events-page-publish-modal">
      <div className="bg-white max-w-lg w-full rounded-xl border border-line shadow-xl">
        <div className="p-6 border-b border-line">
          <div className="label-mono text-terracotta text-[10px]">EVENTS PAGE · BULK PUBLISH</div>
          <h3 className="font-editorial text-2xl tracking-tighter mt-1">
            {result ? "Done." : (scope === "selected" ? "Publish selected sections" : "Publish all sections")}
          </h3>
          <p className="text-xs text-ink/60 mt-2">
            {result
              ? "Global page state is unchanged. Public /events still respects state."
              : "This publishes section content only. Page state (DRAFT / LIVE) is NOT changed here."}
          </p>
        </div>
        <div className="p-6 space-y-3 text-sm">
          {!result && dry && (
            <>
              <Row label="Sections to publish" value={dry.to_publish} />
              <Row label="Already published (skipped)" value={dry.already_published} muted />
              <Row label="Hidden" value={dry.hidden} warn={dry.hidden > 0} />
              <Row label="Missing headline" value={dry.missing_headline} warn={dry.missing_headline > 0} />
              <Row label="Missing image (template requires one)" value={dry.missing_image} warn={dry.missing_image > 0} />
              <div className="pt-2 border-t border-line/50">
                <div className="text-[11px] text-ink/60 mb-1">Translation gaps (not blocking)</div>
                <div className="grid grid-cols-3 gap-2">
                  <MiniRow lang="EN" n={dry.missing_translation_by_lang.en} />
                  <MiniRow lang="DA" n={dry.missing_translation_by_lang.da} />
                  <MiniRow lang="ES" n={dry.missing_translation_by_lang.es} />
                </div>
              </div>
            </>
          )}
          {result && (
            <>
              <Row label="Sections published" value={result.published_count} />
              <Row label="Hidden after publish" value={result.hidden} muted />
              <Row label="Sections with missing headline" value={result.missing_headline} muted />
              <Row label="Sections with missing image" value={result.missing_image} muted />
            </>
          )}
        </div>
        <div className="p-4 border-t border-line flex justify-end gap-2">
          {!result ? (
            <>
              <button onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-widest border border-ink text-ink hover:bg-ink hover:text-white">Cancel</button>
              <button onClick={onConfirm} disabled={busy || (dry?.to_publish || 0) === 0}
                      data-testid="events-page-modal-confirm"
                      className={`px-4 py-2 text-[11px] uppercase tracking-widest ${(dry?.to_publish || 0) === 0 ? "bg-line text-ink/40 cursor-default" : "bg-terracotta text-white hover:bg-terracotta-dark"}`}>
                {busy ? "Publishing…" : `Confirm publish (${dry?.to_publish || 0})`}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-widest bg-ink text-white hover:bg-black">Close</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, muted, warn }) {
  return (
    <div className="flex items-center justify-between">
      <span className={muted ? "text-ink/50" : "text-ink/80"}>{label}</span>
      <span className={`font-editorial text-lg tabular-nums ${warn ? "text-rose-700" : muted ? "text-ink/40" : "text-ink"}`}>{value}</span>
    </div>
  );
}
function MiniRow({ lang, n }) {
  return (
    <div className={`flex flex-col items-center py-2 rounded ${n > 0 ? "bg-amber-50 text-amber-800" : "bg-line/10 text-ink/40"}`}>
      <span className="font-editorial text-xl tabular-nums">{n}</span>
      <span className="text-[10px] label-mono tracking-widest">{lang}</span>
    </div>
  );
}
